import React from 'react';
import { BookOpen } from 'lucide-react';
import { PAGES_IN_BOOK } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import ProgressBar from '../ProgressBar';

interface BookViewProps {
  stats: LifeStats;
}

const BookView: React.FC<BookViewProps> = ({ stats }) => {
  if (stats.lifeExpectancy <= 0) return null;

  // Calculations
  const totalPages = stats.pagesInLifeBook;
  const pagesRead = Math.min(stats.pagesRead, totalPages);
  const percentComplete = (pagesRead / totalPages) * 100;
  const currentPage = Math.floor(pagesRead);
  
  // Visual representation
  const generateBookVisualization = () => {
    // Number of pages to display for visualization
    const displayPageCount = 15;
    const pagesArray = [];
    
    // The current page being read
    const currentPageNumber = Math.floor(stats.yearsLived) + 1;
    
    // Current page progress (for the partial page)
    const currentPageProgress = (stats.yearsLived % 1) * 100;
    
    // Determine which pages to show (range around current page)
    const startPage = Math.max(1, currentPageNumber - Math.floor(displayPageCount / 2));
    const endPage = Math.min(startPage + displayPageCount - 1, Math.ceil(stats.lifeExpectancy));
    
    // Generate individual pages
    for (let i = startPage; i <= endPage; i++) {
      const isCompletedPage = i < currentPageNumber;
      const isCurrentPage = i === currentPageNumber;
      
      // Determine page appearance based on its state
      let pageClass = "bg-white dark:bg-catppuccin-surface1 border border-gray-200 dark:border-catppuccin-surface2";
      if (isCompletedPage) pageClass = "bg-yellow-50 dark:bg-catppuccin-surface2 border border-yellow-200 dark:border-catppuccin-overlay0";
      if (isCurrentPage) pageClass = "bg-white dark:bg-catppuccin-surface0 border-2 border-blue-400 dark:border-catppuccin-blue shadow-md";
      
      // Each page's content
      pagesArray.push(
        <div
          key={`page-${i}`}
          className={`relative w-16 h-24 ${pageClass} rounded-tr-lg rounded-br-lg mx-0.5 shadow transition-all duration-300 hover:scale-110 hover:z-30`}
          style={{
            transformOrigin: 'left center',
            transform: `perspective(600px) rotateY(${(i - startPage) * 5}deg)`,
            zIndex: i
          }}
          title={`Page ${i} of ${Math.ceil(stats.lifeExpectancy)}`}
        >
          {/* Page number */}
          <div className="absolute bottom-2 right-2 text-xs font-medium text-gray-600 dark:text-catppuccin-subtext0">
            {i}
          </div>
          
          {/* Current page marker/bookmark */}
          {isCurrentPage && (
            <div className="absolute -top-3 right-2 text-red-500">
              <div className="w-4 h-10 bg-red-500 rounded-b transform rotate-0"></div>
            </div>
          )}
          
          {/* Content scribbles for read pages */}
          {isCompletedPage && (
            <div className="mt-4 mx-2">
              <div className="h-1 bg-gray-300 dark:bg-catppuccin-overlay0 rounded mb-1 w-3/4"></div>
              <div className="h-1 bg-gray-300 dark:bg-catppuccin-overlay0 rounded mb-1 w-4/5"></div>
              <div className="h-1 bg-gray-300 dark:bg-catppuccin-overlay0 rounded w-2/3"></div>
            </div>
          )}
          
          {/* Current page in-progress content */}
          {isCurrentPage && (
            <div className="mt-4 mx-2">
              <div className="h-1 bg-gray-300 dark:bg-catppuccin-overlay0 rounded mb-1 w-3/4"></div>
              <div className="h-1 bg-gray-300 dark:bg-catppuccin-overlay0 rounded mb-1 w-4/5"></div>
              <div
                className="h-1 bg-blue-400 dark:bg-catppuccin-blue rounded"
                style={{ width: `${currentPageProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="relative flex items-center justify-center pt-10 pb-6 my-6 overflow-x-auto">
        {/* Book spine */}
        <div className="absolute left-1/2 transform -translate-x-1/2 w-3 h-28 bg-gradient-to-r from-blue-700 to-blue-500 dark:from-catppuccin-blue dark:to-catppuccin-sapphire rounded-l z-0"></div>

        {/* Pages */}
        <div className="flex items-center justify-center relative z-10 max-w-full" style={{ marginLeft: '10px' }}>
          {pagesArray}
        </div>
      </div>
    );
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="p-6" animated={true}>
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={24} className="text-green-600 dark:text-catppuccin-green" />
          <h2 className="text-xl font-bold dark:text-catppuccin-text">Your Life Book</h2>
        </div>

        <p className="text-gray-600 dark:text-catppuccin-subtext0 mb-6">
          If your life was represented as a book with {PAGES_IN_BOOK} pages per year, you would be on page {currentPage} 
          of approximately {totalPages} pages.
        </p>
        
        <div className="mb-8">
          <ProgressBar 
            value={pagesRead} 
            maxValue={totalPages} 
            label={`Page ${currentPage} (${percentComplete.toFixed(1)}% complete)`} 
            height="h-4"
            animate={true}
          />
        </div>
        
        {generateBookVisualization()}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="bg-blue-50 dark:bg-catppuccin-surface1 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-700 dark:text-catppuccin-blue mb-1">Current Chapter</h3>
            <p className="text-2xl font-bold text-blue-800 dark:text-catppuccin-text">
              Year {Math.floor(stats.yearsLived) + 1}
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-catppuccin-surface1 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-yellow-700 dark:text-catppuccin-yellow mb-1">Pages Read</h3>
            <p className="text-2xl font-bold text-yellow-800 dark:text-catppuccin-text">
              {Math.floor(pagesRead).toLocaleString()}
            </p>
          </div>

          <div className="bg-green-50 dark:bg-catppuccin-surface1 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-green-700 dark:text-catppuccin-green mb-1">Chapters Completed</h3>
            <p className="text-2xl font-bold text-green-800 dark:text-catppuccin-text">
              {Math.floor(stats.yearsLived)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BookView; 